import { useState, useCallback, useEffect } from 'react';
import { AboutModal, buildAboutConfig } from '@ascend/about-modal';
import { AppLayout } from '@/components/shell/AppLayout';
import { ToastHost } from './components/ui/Toast';
import { ShortcutHelp } from './components/ui/ShortcutHelp';
import { FeedbackForm, FeedbackButton } from './components/FeedbackForm';
import type { FeedbackType } from './utils/userFeedback';
import { setFeedbackOpener } from '@/utils/feedbackBus';
import { setAboutOpener } from '@/utils/aboutBus';
import { useTheme } from './hooks/useTheme';
import appIdentity from 'virtual:app-identity';

const aboutConfig = buildAboutConfig(appIdentity, 'VITE_APP_');

export default function App() {
  useTheme();

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackConfig, setFeedbackConfig] = useState<{
    type?: FeedbackType;
    category?: string;
    error?: string;
  }>({});
  const [aboutOpen, setAboutOpen] = useState(false);

  const handleOpenFeedback = useCallback(
    (type?: FeedbackType, category?: string, error?: string) => {
      setFeedbackConfig({ type, category, error });
      setFeedbackOpen(true);
    },
    [],
  );

  const handleOpenAbout = useCallback(() => setAboutOpen(true), []);

  useEffect(() => {
    setFeedbackOpener(handleOpenFeedback);
    return () => setFeedbackOpener(null);
  }, [handleOpenFeedback]);

  useEffect(() => {
    setAboutOpener(handleOpenAbout);
    return () => setAboutOpener(null);
  }, [handleOpenAbout]);

  const handleCloseFeedback = useCallback(() => {
    setFeedbackOpen(false);
    setFeedbackConfig({});
  }, []);

  const handleCloseAbout = useCallback(() => setAboutOpen(false), []);

  return (
    <>
      <AppLayout />
      <ShortcutHelp />
      <ToastHost />
      <FeedbackForm
        isOpen={feedbackOpen}
        onClose={handleCloseFeedback}
        initialType={feedbackConfig.type}
        initialCategory={feedbackConfig.category}
        initialError={feedbackConfig.error}
      />
      <FeedbackButton onOpen={handleOpenFeedback} />
      <AboutModal
        isOpen={aboutOpen}
        onClose={handleCloseAbout}
        config={aboutConfig}
      />
    </>
  );
}
